import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Heart, Activity, BellOff, RadioTower } from "lucide-react";
import { toast } from "sonner";

// Visual config per severity
const SEV = {
  critical: {
    label: "CRITICAL",
    bg:      "bg-rose-500/10 border-rose-500/40 hover:bg-rose-500/15",
    text:    "text-rose-300",
    dot:     "bg-rose-500",
    pulse:   "animate-[pulse_1.4s_ease-in-out_infinite]",
    rank:    3,
  },
  hot: {
    label: "HOT",
    bg:      "bg-amber-500/10 border-amber-500/40 hover:bg-amber-500/15",
    text:    "text-amber-300",
    dot:     "bg-amber-400",
    pulse:   "animate-[pulse_2s_ease-in-out_infinite]",
    rank:    2,
  },
  warm: {
    label: "WARM",
    bg:      "bg-cyan-500/10 border-cyan-500/30 hover:bg-cyan-500/15",
    text:    "text-cyan-300",
    dot:     "bg-cyan-400",
    pulse:   "",
    rank:    1,
  },
};

function Initials({ name = "" }) {
  const ini = name.split(" ").map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";
  return (
    <span className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold bg-white/10 text-white/80 shrink-0">
      {ini}
    </span>
  );
}

export default function HotPitcherTile({ pollMs = 20000 }) {
  const [data, setData] = useState(null);
  const [silencing, setSilencing] = useState({});
  const timer = useRef(null);

  const fetchNow = async () => {
    try {
      const r = await api.get("/hot-pitchers");
      setData(r.data);
    } catch (e) { /* swallow — keep last good data */ }
  };

  useEffect(() => {
    fetchNow();
    timer.current = setInterval(fetchNow, pollMs);
    return () => clearInterval(timer.current);
  }, [pollMs]);

  const dismiss = async (pid, name) => {
    setSilencing((s) => ({ ...s, [pid]: true }));
    try {
      await api.post("/hot-pitchers/dismiss", { player_user_id: pid });
      toast.success(`${name} silenced for 10 minutes`);
      fetchNow();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not dismiss");
    } finally {
      setSilencing((s) => ({ ...s, [pid]: false }));
    }
  };

  const pitchers = data?.pitchers || [];
  const top = pitchers[0]; // for the headline pulse

  return (
    <div
      className={`rounded-xl ink-card p-5 transition-colors ${top ? SEV[top.severity].bg : ""}`}
      data-testid="hot-pitcher-tile"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className={`relative inline-flex h-2.5 w-2.5 rounded-full ${top ? SEV[top.severity].dot : "bg-emerald-400"} ${top ? SEV[top.severity].pulse : ""}`}
            data-testid="hot-pitcher-status-dot"
          />
          <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">
            Hot-Pitcher (live)
          </div>
        </div>
        <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">
          {pitchers.length === 0 ? "all clear" : `${pitchers.length} active`}
        </span>
      </div>

      {pitchers.length === 0 ? (
        <div className="mt-4 py-3 flex items-center gap-3 text-white/50 text-sm">
          <RadioTower className="h-4 w-4 text-emerald-400" />
          <span>No fatigue spikes in the dugout right now. Watches will ping here in real time.</span>
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          {pitchers.map((p) => {
            const s = SEV[p.severity] || SEV.warm;
            return (
              <div
                key={p.player_user_id}
                className={`rounded-lg border ${s.bg} px-3 py-2.5 flex items-center gap-3`}
                data-testid={`hot-pitcher-row-${p.player_user_id}`}
              >
                <Initials name={p.player_name} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/dashboard/player/${p.player_user_id}`}
                      className="font-semibold text-white truncate hover:underline"
                      data-testid={`hot-pitcher-link-${p.player_user_id}`}
                    >
                      {p.player_name}
                    </Link>
                    <span className={`text-[9px] tracking-[0.15em] font-extrabold px-1.5 py-0.5 rounded ${s.text} border ${s.text.replace("text-", "border-")}`}>
                      {s.label}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-xs text-white/70 font-mono-num">
                    <span className="flex items-center gap-1">
                      <Heart className="h-3 w-3 text-rose-300" />
                      <b>{p.current_hr ?? "—"}</b> bpm
                      {p.hr_delta !== null && (
                        <span className="text-white/40 ml-0.5">
                          (<span className={p.hr_delta > 0 ? "text-rose-300" : "text-emerald-300"}>{p.hr_delta > 0 ? "+" : ""}{p.hr_delta}</span> vs {p.baseline_rhr})
                        </span>
                      )}
                    </span>
                    {p.current_hrv !== null && (
                      <span className="flex items-center gap-1">
                        <Activity className="h-3 w-3 text-cyan-300" />
                        HRV <b>{p.current_hrv}</b>
                        {p.hrv_drop_pct !== null && (
                          <span className={`ml-0.5 ${p.hrv_drop_pct > 0 ? "text-rose-300" : "text-emerald-300"}`}>
                            ({p.hrv_drop_pct > 0 ? "−" : "+"}{Math.abs(Math.round(p.hrv_drop_pct * 100))}%)
                          </span>
                        )}
                      </span>
                    )}
                    {p.minutes_in_zone > 0 && (
                      <span className="text-white/50">{p.minutes_in_zone}m in zone</span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => dismiss(p.player_user_id, p.player_name)}
                  disabled={!!silencing[p.player_user_id]}
                  className="shrink-0 rounded p-1.5 hover:bg-white/10 text-white/50 hover:text-white/80 disabled:opacity-40"
                  title="Silence for 10 minutes"
                  data-testid={`hot-pitcher-dismiss-${p.player_user_id}`}
                >
                  <BellOff className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-white/30">
        <span>Refreshes every {Math.round(pollMs / 1000)}s · Apple Watch / Health Connect</span>
        <Link to="/dashboard/replays" className="hover:text-white/60" data-testid="hot-pitcher-replays-link">
          Replay archive →
        </Link>
      </div>
    </div>
  );
}
